<template>
    <CqModal
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Stempeldienst bewerken"
    >
        <InfoGrid>
            <InfoHeader>Werknemer</InfoHeader><span>{{ resourceDisplayName }}</span>
            <InfoHeader>Datum</InfoHeader><span>{{ dateString }}</span>
            <CqSelectInput label="Rooster" v-if="resource" class="infoGridInformationWithoutHeader" :options="filteredRosters" v-model="selectedRosterId" @update:modelValue="changedRoster" noMargin />
            <CqSelectInput label="Dienst" v-if="resource" class="infoGridInformationWithoutHeader" :options="filterShifts" v-model="personalStampShift.shiftId" noMargin />
            <template v-if="shifts[personalStampShift.shiftId] && shifts[personalStampShift.shiftId].template!.legend">
                <InfoHeader>Legenda</InfoHeader><span>{{ shifts[personalStampShift.shiftId].template!.legend }}</span>
            </template>
        </InfoGrid>
        <WishInformation :activeWish="activeWish" :date="date" :shifts="shifts" />
        <CqButtonBar>
            <CqButton @click="closeModal" label="Annuleren" :disabled="isSaving" />
            <CqButton @click="save" primary :disabled="isSaving || personalStampShift.shiftId === 0" label="Opslaan" icon="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal, CqSelectInput, NumberSelectInputOption} from '@cquential/ui';
    import WishInformation from '@/components/modals/WishInformation.vue';
    import {RosterSelectOption, PermissionsForRoster} from '@/types/types';
    import {Shift, PersonalPlanningWish, PersonalStamp, PersonalStampShift} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqSelectInput,
            WishInformation,
            InfoGrid,
            InfoHeader,
        },
        emits: [
            'create:stampShift',
            'delete:stampShift',
            'update:stampShift',
        ],
    })
    export default class StampShiftEditModal extends Vue {
        private static instance: StampShiftEditModal | null = null;

        @Prop({required: true}) public rosters!: RosterSelectOption[];
        @Prop({type: Number, required: true}) public rosterId!: number;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public shiftIdsPerRoster!: Record<number, number[]>;
        @Prop({required: true}) public defaultShiftIdPerRoster!: Record<number, number>;
        @Prop({required: true}) public permissionsPerRoster!: Record<number, PermissionsForRoster>;

        private show: boolean = false;
        private isSaving: boolean = false;
        private currentOrganisationsId: string = '';
        private dateString: string = '';
        private selectedRosterId: number = 0;
        private personalStampShift: PersonalStampShift = {
            stampId: 0,
            dayIndex: 0,
            shiftId: 0,
        };

        private resource: ResourceWithKeyedByDateObjects | null = null;
        private date: Date = new Date();

        public get resourceDisplayName(): string {
            return this.resource ? ResourceUtil.displayName(this.resource) : '';
        }

        public mounted(): void {
            StampShiftEditModal.instance = this;
        }

        public beforeUnmount(): void {
            StampShiftEditModal.instance = null;
        }

        public openModal(openResource: ResourceWithKeyedByDateObjects, openPersonalStamp: PersonalStamp | null, openPersonalStampShift: PersonalStampShift | null, openDate: Date): void {
            if (openPersonalStamp === null && openPersonalStampShift === null) {
                throw new Error('Either the stamp you\'re adding to, or the shift you\'re editing, must be supplied.');
            }

            /** @see KeyboardDeploymentAutocomplete.disable() */
            // @ts-expect-error TS2339
            KeyboardDeploymentAutocomplete.__vfdConstructor.disable();

            this.setCurrentOrganisationsId(this.rosterId);
            this.resource = openResource;
            this.show = true;
            this.isSaving = false;
            this.date = new Date(openDate);

            let initialRosterId: number;
            if (openPersonalStampShift !== null) {
                if (this.shifts[openPersonalStampShift.shiftId].rosterId) {
                    initialRosterId = this.shifts[openPersonalStampShift.shiftId].rosterId;
                } else {
                    initialRosterId = this.rosterId;
                }

                this.personalStampShift = {...openPersonalStampShift};
            } else { // personalStamp !== null
                initialRosterId = this.rosterId;

                this.personalStampShift = {
                    shiftId: this.defaultShiftIdPerRoster[initialRosterId],
                    dayIndex: Dates.Utils.diffInDays(openDate, openPersonalStamp!.beginDate) % openPersonalStamp!.iterationLength,
                    stampId: openPersonalStamp!.id!,
                };
            }

            this.dateString = Dates.Formatter.formatDateLong(openDate);
            this.selectedRosterId = initialRosterId;
        }

        public get filteredRosters(): NumberSelectInputOption[] {
            return this.rosters.filter((roster: RosterSelectOption): boolean => {
                // TODO: as the date to check, use the first date that is being edited
                if (roster.data.beginDate && roster.data.beginDate > this.date) {
                    return false;
                }

                if (!this.overlappingFunction(roster)) {
                    return false;
                }

                return roster.data.id in this.permissionsPerRoster && this.permissionsPerRoster[roster.data.id][UserPermissions.EditStampPeriod];
            }).map((roster: RosterSelectOption): NumberSelectInputOption => {
                return {
                    value: roster.data.id,
                    label: roster.data.name,
                };
            });
        }

        public get activeWish(): PersonalPlanningWish | undefined {
            if (this.resource === null || !this.resource.personalPlanningWishes) {
                return undefined;
            }

            for (const wish of this.resource.personalPlanningWishes) {
                if ((wish.recursWeekly && (wish.recursOnDay % 7) === Dates.Utils.getDay(this.date)) || (wish.date && wish.date.valueOf() === this.date.valueOf())) {
                    return wish;
                }
            }

            return undefined;
        }

        public get filterShifts(): NumberSelectInputOption[] {
            // Define array to return
            const filteredShifts: NumberSelectInputOption[] = [];
            // Created date object of given date
            // Loop

            if (!(this.selectedRosterId in this.shiftIdsPerRoster)) {
                return [];
            }

            for (const shiftId of this.shiftIdsPerRoster[this.selectedRosterId]) {
                if (this.date >= this.shifts[shiftId].beginDate && this.date <= this.shifts[shiftId].endDate) {
                    filteredShifts.push({
                        label: this.shifts[shiftId].template!.code,
                        value: Number(shiftId),
                    });
                }
            }
            // Sort array alphabetically by value
            filteredShifts.sort((a: NumberSelectInputOption, b: NumberSelectInputOption): number => a.label.localeCompare(b.label));

            // Return filtered array of objects
            return filteredShifts;
        }

        public overlappingFunction(singleRoster: RosterSelectOption): boolean {
            if (!this.resource) {
                return false;
            }
            // If current active roster, return true
            if (singleRoster.value === this.rosterId) {
                return true;
            } else {
                for (const index in this.resource.functions) {
                    if (singleRoster.data.allowedFunctions && singleRoster.data.allowedFunctions[this.resource.functions[Number(index)].id!]) {
                        return true;
                    }
                }
            }

            return false;
        }

        // On  change roster
        public changedRoster(rosterId: string | number | null): void {
            if (this.filterShifts[0].value) {
                this.personalStampShift.shiftId = this.filterShifts[0].value as number;
            } else {
                this.personalStampShift.shiftId = 0;
            }
            if (rosterId !== null) {
                this.setCurrentOrganisationsId(Number(rosterId));
            }
        }

        // Set current organisationId
        public setCurrentOrganisationsId(rosterId: number): void {
            for (const roster of this.rosters) {
                if (roster.value === rosterId) {
                    this.currentOrganisationsId = roster.data.organisationId;
                    break;
                }
            }
        }

        public save(): void {
            if (this.isSaving) {
                return;
            }
            this.isSaving = true;

            if (!this.personalStampShift || !this.personalStampShift.id) {
                this.saveNewStampShift();
            } else {
                this.saveUpdatedStampShift();
            }

            this.closeModal();
        }

        public saveNewStampShift(): void {
            this.$emit('create:stampShift', {shift: this.personalStampShift, resourceId: this.resource!.id!});
        }

        public saveUpdatedStampShift(): void {
            if (!this.resource) {
                throw new Error('We need a resource in order to save stamp shifts.');
            }

            if (this.isDefaultShift) {
                if ('id' in this.personalStampShift) {
                    this.$emit('delete:stampShift', {shift: this.personalStampShift, resourceId: this.resource!.id!});
                }
            } else {
                this.$emit('update:stampShift', {shift: this.personalStampShift, resourceId: this.resource!.id!});
            }
        }

        public get isDefaultShift(): boolean {
            return this.shifts[this.personalStampShift.shiftId].template!.isDefault;
        }

        public closeModal(): void {
            this.show = false;
        }

        public static openModal(resource: ResourceWithKeyedByDateObjects, personalStamp: PersonalStamp | null, personalStampShift: PersonalStampShift | null, date: Date): void {
            this.instance!.openModal(resource, personalStamp, personalStampShift, date);
        }
    }
</script>

<style lang="scss" scoped>
    .infoGridInformationWithoutHeader {
        grid-column: span 2;
    }
</style>
